import React, {FC, useEffect, useMemo, useState} from 'react';
import {TagItemViewChildrenWrapper} from './styles';
import {useTheme} from "styled-components";
import {IMasterTagTypes, MasterTag, ShowMoreButton, useBreakpoint} from '@brg/ui';
import {Tag} from '@brg/api';
import {bTag} from 'core/utils';
import {useTranslation} from 'react-i18next';
import {ITagsProps} from 'shared/hooks/api/useTagsList';
import {useRouteMatch} from 'react-router-dom';
import {isRequired} from "../../../shared/modals/selectTagsModal/support/isRequired";
import {useGetCurrentTeam} from "../../../shared/hooks/useGetCurrentTeam";
import {useGetOrganizationEntity} from "../../../shared/hooks/api/useGetOrganizationEntity";
import {AppTester} from 'supporting';
import {useGetTagCategories} from "../../../shared/hooks/api/useGetTagCategories";
import {AddTagField} from "../../../shared/modals/selectTagsModal/AddTagField";
import {useGetSelectedTagColor} from "../../../shared/hooks/useGetSelectedTagColor";
import { CreateLinkTo } from 'shared/constants/linkTo';
import { Link } from 'shared/components/link/Link';

const MAX_CHILDRENS_SLICE = 12;


/**
 * Свойства Burgundy Tag
 */
export interface BTagProps {
  image?: string | JSX.Element;
  selected?: Function;
  disabled?: boolean;
  parent?: boolean;
  isTagOpen?: boolean;
  removable?: Function;
  secondary?: boolean;
  educational?: boolean;
  parentTag?: Tag;
  onClick?: (tag: Tag, selected: boolean) => void;
  onParentClick?: (tag: Tag, selected: boolean) => void;
  onMoreClick?: (ev: any) => void;
  onTagArrowClick?: (ev: any) => void;
  onSearchInputChange?: (ev: any) => void;
  onFocus?: (ev: any) => void;
  variant?: string;
  children_item?: any[];
  isSearching?: boolean;
  searchNeeded?: boolean;
  moreNeeded?: boolean;
  opacity?: boolean;
  entityTypes?: any;
  parentTagId?: string;
  isEmptySearch?: boolean;
  tagsFilter?: ITagsProps;
  isEditPage?: boolean;
  mobileArrowVisible?: boolean;
  search?: string;
  hasLink?: boolean;
}

/**
 * Burgundy Tag
 *
 * @param image     - изображение если нужно
 * @param children  - название тега (текст)
 * @param selected  - выбран или нет
 * @param disabled  - выключен
 * @param parent    - имеет потомков
 * @param removable - можно удалить
 * @param secondary - вторичный
 * @param onClick   - событие при клике
 * @param onParentClick   - событие при клике на родителе тэга
 * @param onMoreClick   - событие при клике на кнопку more внутри тэга
 * @param onTagArrowClick   - событие при клике на иконку стрелки у тэга
 * @param onSearchInputChange   - событие поиска внутри тэга
 * @param variant - вариант дизайна
 * @param isSearching - идет ли поиск по тэгу в данный момент
 * @param mobileArrowVisible - показывать иконку стрелки на мобилке (страница категории тега)
 * @param searchInsideNeeded - нужен ли поиск внутри тэга
 * @param educational - является ли тег образовательным
 * @param entityTypes - entityTypes для создании нового тега
 * @param parentTagId - при создании нового тега используется тег родителя
 * @param isEditPage - страница созданиия/редактирования проекта
 * @param search - строка в общем поиске по тегам
 * @constructor
 */
export const BTagWithChildren: FC<React.PropsWithChildren<BTagProps>> = ({
  children,
  parentTag,
  children_item,
  selected,
  disabled,
  educational,
  onClick,
  onMoreClick,
  onTagArrowClick,
  moreNeeded: moreNeededProp,
  isSearching,
  opacity,
  entityTypes,
  parentTagId,
  isEmptySearch,
  tagsFilter,
  search,
  hasLink,
}) => {

  const { isDarkText } = useTheme();
  const { t } = useTranslation();
  const tagCategories = useGetTagCategories(parentTag)
  const hasChildren = useMemo(() => children_item && children_item.length > 0,  [children_item])
  const showChildren = useMemo(() => hasChildren,  [hasChildren])
  const [isMorePushed, setMorePushed] = useState(false);
  const hasChildTag = children_item?.some(item => item.name === tagsFilter?.search?.[0]);

  const [moreNeeded, setMoreNeeded] = useState(false)
  useEffect(() => {
    moreNeededProp && setMoreNeeded(true)
  }, [moreNeededProp]);

  const isNewTag = (isEmptySearch || parentTag?.typeCategory === 'open') && tagsFilter?.groupIds?.length && tagsFilter?.search?.length && !hasChildTag
  const childrens = useMemo(() => {
    if (isNewTag) return [];

    if (showChildren && moreNeeded && !isMorePushed && !isSearching) {
      return children_item?.slice(0, MAX_CHILDRENS_SLICE) || []
    }
    return children_item || [];

  } , [showChildren, moreNeeded, isMorePushed, isSearching, children_item])


  const tooltipProps = useMemo(() => (educational ? {
    'data-tip':  tagCategories || t('designer_edit.tips.education_tag'),
    'data-type': isDarkText ? 'dark' : 'light',
    'data-education': 'true',
    'data-for': 'tooltip-parent-element'
  } : {
    'data-tip':  tagCategories || '',
    'data-type': isDarkText ? 'dark' : 'light',
    'data-default': 'true',
    'data-for': 'tooltip-parent-element'
  }) , [tagCategories, isDarkText, educational, t, isMorePushed, showChildren, childrens?.length])

  const isProjectEditPage = !!useRouteMatch('/edit');
  const currentTeam = useGetCurrentTeam();
  const organizationEntity = useGetOrganizationEntity(currentTeam?.id || '');
  const newTagEntityTypes = isNewTag && parentTag?.entityTypes

  //@ts-ignore
  const selectedColor = useGetSelectedTagColor();

  const childrensElements = useMemo(() => {

    return !!childrens?.length ? childrens.map((tag) => {
      return {
      ...tooltipProps,
      ...tag,
        //@ts-ignore
        onClick: () => onClick && onClick(tag, selected(tag?.tagId)),
        children: isProjectEditPage ? (
          <>{bTag.title(tag) || ''}</>
        ) : (
          <Link 
            to={CreateLinkTo.tag(tag?.tagId)} 
            disabled={!tag?.tagId || !hasLink} 
            type="tag"  
            // @ts-ignore
            style={{ color: 'inherit', textDecoration: 'none' }} 
            key={`link-${tag?.tagId}`}
          >
            {bTag.title(tag) || ''}
          </Link>
        ),
        active: selected && selected(tag?.tagId),
        activeColorTheme: selectedColor
      }
    }) : undefined

  }, [childrens, onClick, selected, tooltipProps])

  const innerContent = <>
    {
        isNewTag && <AddTagField
            parentTag={{
              // @ts-ignore
              tagId: parentTagId,
              entityTypes,
            }}
            name={(tagsFilter?.search?.length && tagsFilter?.search[0]) || ''}
            // @ts-ignore
            newTagEntityTypes={newTagEntityTypes}
        />
    }
    {(showChildren && moreNeeded && !isSearching && !search?.trim()) && <ShowMoreButton showLess={isMorePushed}
        onClick={(e) => {
          if (!isMorePushed) onMoreClick && onMoreClick(e);
          setMorePushed(prev => !prev);
        }}
    >{isMorePushed ? t("tags_button_less") : t("tags_button_more")}</ShowMoreButton>}
  </>

  return <MasterTag
      {...tooltipProps}
      type={!!childrens?.length ? IMasterTagTypes.group_with_children : IMasterTagTypes.simple}
      onClick={() => {
        if (!!onClick && parentTag && selected) {
          onClick(parentTag, selected(parentTag?.tagId))
          return;
        }
      }}
      hideGridTagBreaker
      onArrowClick={(e) => onTagArrowClick && onTagArrowClick(e)}
      disabled={disabled}
      colorTheme={selected && (selected(parentTag?.tagId) ? selectedColor : undefined)}
      activeColorTheme={selected && (selected(parentTag?.tagId) ? selectedColor : undefined)}
      // @ts-ignore
      appearance={selected && (selected(parentTag?.tagId) ? 'filled' : undefined)}
      rightContent={<>| {children_item?.length}</>}
      elements={childrensElements}
      innerContent={innerContent}
  >
    {children}
  </MasterTag>
};
import React, {FC, useEffect, useMemo, useState} from 'react';
import {TagItemViewChildrenWrapper} from './styles';
import {useTheme} from "styled-components";
import {IMasterTagTypes, MasterTag, ShowMoreButton, useBreakpoint} from '@brg/ui';
import {Tag} from '@brg/api';
import {bTag} from 'core/utils';
import {useTranslation} from 'react-i18next';
import {ITagsProps} from 'shared/hooks/api/useTagsList';
import {useRouteMatch} from 'react-router-dom';
import {isRequired} from "../../../shared/modals/selectTagsModal/support/isRequired";
import {useGetCurrentTeam} from "../../../shared/hooks/useGetCurrentTeam";
import {useGetOrganizationEntity} from "../../../shared/hooks/api/useGetOrganizationEntity";
import {AppTester} from 'supporting';
import {useGetTagCategories} from "../../../shared/hooks/api/useGetTagCategories";
import {AddTagField} from "../../../shared/modals/selectTagsModal/AddTagField";
import {useGetSelectedTagColor} from "../../../shared/hooks/useGetSelectedTagColor";
import { CreateLinkTo } from 'shared/constants/linkTo';
import { Link } from 'shared/components/link/Link';

const MAX_CHILDRENS_SLICE = 12;


/**
 * Свойства Burgundy Tag
 */
export interface BTagProps {
  image?: string | JSX.Element;
  selected?: Function;
  disabled?: boolean;
  parent?: boolean;
  isTagOpen?: boolean;
  removable?: Function;
  secondary?: boolean;
  educational?: boolean;
  parentTag?: Tag;
  onClick?: (tag: Tag, selected: boolean) => void;
  onParentClick?: (tag: Tag, selected: boolean) => void;
  onMoreClick?: (ev: any) => void;
  onTagArrowClick?: (ev: any) => void;
  onSearchInputChange?: (ev: any) => void;
  onFocus?: (ev: any) => void;
  variant?: string;
  children_item?: any[];
  isSearching?: boolean;
  searchNeeded?: boolean;
  moreNeeded?: boolean;
  opacity?: boolean;
  entityTypes?: any;
  parentTagId?: string;
  isEmptySearch?: boolean;
  tagsFilter?: ITagsProps;
  isEditPage?: boolean;
  mobileArrowVisible?: boolean;
  search?: string;
  hasLink?: boolean;
}

/**
 * Burgundy Tag
 *
 * @param image     - изображение если нужно
 * @param children  - название тега (текст)
 * @param selected  - выбран или нет
 * @param disabled  - выключен
 * @param parent    - имеет потомков
 * @param removable - можно удалить
 * @param secondary - вторичный
 * @param onClick   - событие при клике
 * @param onParentClick   - событие при клике на родителе тэга
 * @param onMoreClick   - событие при клике на кнопку more внутри тэга
 * @param onTagArrowClick   - событие при клике на иконку стрелки у тэга
 * @param onSearchInputChange   - событие поиска внутри тэга
 * @param variant - вариант дизайна
 * @param isSearching - идет ли поиск по тэгу в данный момент
 * @param mobileArrowVisible - показывать иконку стрелки на мобилке (страница категории тега)
 * @param searchInsideNeeded - нужен ли поиск внутри тэга
 * @param educational - является ли тег образовательным
 * @param entityTypes - entityTypes для создании нового тега
 * @param parentTagId - при создании нового тега используется тег родителя
 * @param isEditPage - страница созданиия/редактирования проекта
 * @param search - строка в общем поиске по тегам
 * @constructor
 */
export const BTagWithChildren: FC<React.PropsWithChildren<BTagProps>> = ({
  children,
  parentTag,
  children_item,
  selected,
  disabled,
  educational,
  onClick,
  onMoreClick,
  onTagArrowClick,
  moreNeeded: moreNeededProp,
  isSearching,
  opacity,
  entityTypes,
  parentTagId,
  isEmptySearch,
  tagsFilter,
  search,
  hasLink,
}) => {

  const { isDarkText } = useTheme();
  const { t } = useTranslation();
  const tagCategories = useGetTagCategories(parentTag)
  const hasChildren = useMemo(() => children_item && children_item.length > 0,  [children_item])
  const showChildren = useMemo(() => hasChildren,  [hasChildren])
  const [isMorePushed, setMorePushed] = useState(false);
  const hasChildTag = children_item?.some(item => item.name === tagsFilter?.search?.[0]);

  const [moreNeeded, setMoreNeeded] = useState(false)
  useEffect(() => {
    moreNeededProp && setMoreNeeded(true)
  }, [moreNeededProp]);

  const isNewTag = (isEmptySearch || parentTag?.typeCategory === 'open') && tagsFilter?.groupIds?.length && tagsFilter?.search?.length && !hasChildTag
  const childrens = useMemo(() => {
    if (isNewTag) return [];

    if (showChildren && moreNeeded && !isMorePushed && !isSearching) {
      return children_item?.slice(0, MAX_CHILDRENS_SLICE) || []
    }
    return children_item || [];

  } , [showChildren, moreNeeded, isMorePushed, isSearching, children_item])


  const tooltipProps = useMemo(() => (educational ? {
    'data-tip':  tagCategories || t('designer_edit.tips.education_tag'),
    'data-type': isDarkText ? 'dark' : 'light',
    'data-education': 'true',
    'data-for': 'tooltip-parent-element'
  } : {
    'data-tip':  tagCategories || '',
    'data-type': isDarkText ? 'dark' : 'light',
    'data-default': 'true',
    'data-for': 'tooltip-parent-element'
  }) , [tagCategories, isDarkText, educational, t, isMorePushed, showChildren, childrens?.length])

  const isProjectEditPage = !!useRouteMatch('/edit');
  const currentTeam = useGetCurrentTeam();
  const organizationEntity = useGetOrganizationEntity(currentTeam?.id || '');
  const newTagEntityTypes = isNewTag && parentTag?.entityTypes

  //@ts-ignore
  const selectedColor = useGetSelectedTagColor();

  const childrensElements = useMemo(() => {

    return !!childrens?.length ? childrens.map((tag) => {
      return {
      ...tooltipProps,
      ...tag,
        //@ts-ignore
        onClick: () => onClick && onClick(tag, selected(tag?.tagId)),
        children: isProjectEditPage ? (
          <>{bTag.title(tag) || ''}</>
        ) : (
          <Link 
            to={CreateLinkTo.tag(tag?.tagId)} 
            disabled={!tag?.tagId || !hasLink} 
            type="tag"  
            // @ts-ignore
            style={{ color: 'inherit', textDecoration: 'none' }} 
            key={`link-${tag?.tagId}`}
          >
            {bTag.title(tag) || ''}
          </Link>
        ),
        active: selected && selected(tag?.tagId),
        activeColorTheme: selectedColor
      }
    }) : undefined

  }, [childrens, onClick, selected, tooltipProps])

  const innerContent = <>
    {
        isNewTag && <AddTagField
            parentTag={{
              // @ts-ignore
              tagId: parentTagId,
              entityTypes,
            }}
            name={(tagsFilter?.search?.length && tagsFilter?.search[0]) || ''}
            // @ts-ignore
            newTagEntityTypes={newTagEntityTypes}
        />
    }
    {(showChildren && moreNeeded && !isSearching && !search?.trim()) && <ShowMoreButton showLess={isMorePushed}
        onClick={(e) => {
          if (!isMorePushed) onMoreClick && onMoreClick(e);
          setMorePushed(prev => !prev);
        }}
    >{isMorePushed ? t("tags_button_less") : t("tags_button_more")}</ShowMoreButton>}
  </>

  return <MasterTag
      {...tooltipProps}
      type={!!childrens?.length ? IMasterTagTypes.group_with_children : IMasterTagTypes.simple}
      onClick={() => {
        if (!!onClick && parentTag && selected) {
          onClick(parentTag, selected(parentTag?.tagId))
          return;
        }
      }}
      hideGridTagBreaker
      onArrowClick={(e) => onTagArrowClick && onTagArrowClick(e)}
      disabled={disabled}
      colorTheme={selected && (selected(parentTag?.tagId) ? selectedColor : undefined)}
      activeColorTheme={selected && (selected(parentTag?.tagId) ? selectedColor : undefined)}
      // @ts-ignore
      appearance={selected && (selected(parentTag?.tagId) ? 'filled' : undefined)}
      rightContent={<>| {children_item?.length}</>}
      elements={childrensElements}
      innerContent={innerContent}
  >
    {children}
  </MasterTag>
};
